/**
 * Data Import API client. Mirrors the DataImportViewSet in
 * apexos/apps/imports/views.py.
 */
import { authService } from './auth';
import type {
  DataImport,
  RewindPreview,
  ImportOptions,
  ImportPreset,
  ImportType,
  ImportRowIssue,
} from '../types/imports';

const API_BASE_URL = window.location.origin;

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

class ImportsApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { isFormData?: boolean } = {},
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const accessToken = authService.getAccessToken();

    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      ...(options.headers as Record<string, string>),
    };
    if (!options.isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    let response = await fetch(url, { ...options, headers, credentials: 'include' });

    if (response.status === 401) {
      try {
        const newToken = await authService.refreshAccessToken();
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers, credentials: 'include' });
      } catch {
        window.location.href = '/login';
        throw new Error('Authentication required');
      }
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw Object.assign(new Error(body.detail || body.message || (Array.isArray(body) ? body.join(" ") : `API Error: ${response.status}`)), { preview: body.preview });
    }

    // Some endpoints (errors.csv) return non-JSON — callers use the raw URL.
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return undefined as unknown as T;
    }
    return response.json() as Promise<T>;
  }

  // ----- Presets -----

  async listPresets(): Promise<{ presets: ImportPreset[] }> {
    return this.request('/api/imports/presets/');
  }

  // ----- Imports CRUD -----

  async listImports(): Promise<PaginatedResponse<DataImport> | DataImport[]> {
    return this.request('/api/imports/');
  }

  async getImport(id: number): Promise<DataImport> {
    return this.request(`/api/imports/${id}/`);
  }

  async createImport(params: {
    importType: ImportType;
    file: File;
    options?: ImportOptions;
  }): Promise<DataImport> {
    const form = new FormData();
    form.append('import_type', params.importType);
    form.append('source_file', params.file);
    if (params.options) {
      form.append('options', JSON.stringify(params.options));
    }
    return this.request('/api/imports/', {
      method: 'POST',
      body: form,
      isFormData: true,
    });
  }

  async updateImport(
    id: number,
    payload: Partial<Pick<DataImport, 'column_mapping' | 'options' | 'preset'>>,
  ): Promise<DataImport> {
    return this.request(`/api/imports/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async dryRunImport(id: number): Promise<DataImport> {
    return this.request(`/api/imports/${id}/dry-run/`, { method: 'POST' });
  }

  async previewRewind(id: number): Promise<RewindPreview> {
    return this.request(`/api/imports/${id}/rewind-preview/`);
  }

  async rewindImport(id: number, token: string): Promise<DataImport> {
    return this.request(`/api/imports/${id}/rewind/`, {
      method: 'POST', body: JSON.stringify({ token, confirm: true }),
    });
  }

  async executeImport(id: number): Promise<DataImport> {
    return this.request(`/api/imports/${id}/execute/`, { method: 'POST' });
  }

  async listImportErrors(
    id: number,
  ): Promise<PaginatedResponse<ImportRowIssue> | ImportRowIssue[]> {
    return this.request(`/api/imports/${id}/errors/`);
  }

  errorsCsvUrl(id: number): string {
    return `${this.baseUrl}/api/imports/${id}/errors.csv/`;
  }

  async getColumnValues(
    id: number,
    header: string,
  ): Promise<{
    header: string;
    total_unique: number;
    values: { value: string; count: number }[];
  }> {
    const q = new URLSearchParams({ header });
    return this.request(`/api/imports/${id}/column-values/?${q.toString()}`);
  }

  // ----- Boards (reuse existing endpoints on other apps) -----

  async listJobBoards(): Promise<
    { id: number; name: string; color: string }[]
  > {
    const resp = await this.request<any>('/api/boards/');
    return Array.isArray(resp) ? resp : resp.results || [];
  }

  async listJobBoardStatuses(
    boardId: number,
  ): Promise<{ id: number; name: string; order: number | null; board: number }[]> {
    const resp = await this.request<any>(`/api/board-statuses/?board=${boardId}`);
    return Array.isArray(resp) ? resp : resp.results || [];
  }

  async listOpportunityBoards(): Promise<
    { id: number; name: string; color: string }[]
  > {
    const resp = await this.request<any>('/api/crm/opportunity-boards/');
    return Array.isArray(resp) ? resp : resp.results || [];
  }

  async listOpportunityBoardStatuses(
    boardId: number,
  ): Promise<{ id: number; name: string; order: number | null; board: number }[]> {
    const resp = await this.request<any>(
      `/api/crm/opportunity-board-statuses/?board=${boardId}`,
    );
    return Array.isArray(resp) ? resp : resp.results || [];
  }
}

export const importsApi = new ImportsApiClient();
